import { AbstractConnector } from '@web3-react/abstract-connector'
import { ConnectorUpdate } from '@web3-react/types'
import { Eip6963Provider, Eip6963ProviderDetail, findEip6963ProviderByRdns } from './eip6963'

export class NoEip6963ProviderError extends Error {
  constructor(public readonly walletName: string) {
    super(`${walletName} is no longer available. Make sure the extension is enabled, then reload the page.`)
    this.name = 'NoEip6963ProviderError'
  }
}

export class UserRejectedEip6963RequestError extends Error {
  constructor() {
    super('Connection request was rejected in your wallet.')
    this.name = 'UserRejectedEip6963RequestError'
  }
}

// EIP-1193 error codes.
const USER_REJECTED_REQUEST = 4001

function parseChainId(chainId: string | number): number {
  return typeof chainId === 'string' ? Number.parseInt(chainId, 16) : chainId
}

/**
 * web3-react v6 connector bound to one specific EIP-6963-announced wallet.
 *
 * Unlike `InjectedConnector`, which always reaches for the shared `window.ethereum`
 * slot, this targets the exact provider handle the wallet announced. That is what
 * makes it possible to have MetaMask and Rabby (or OKX, or Binance Web3) installed
 * side by side and connect to whichever one the user actually clicked.
 *
 * The provider is resolved by `rdns` at activation time rather than captured in the
 * constructor, so a wallet that re-announces (extension reload, page-lifecycle churn)
 * still connects against a live handle instead of a stale one.
 */
export class Eip6963Connector extends AbstractConnector {
  private readonly rdns: string

  private readonly walletName: string

  private provider?: Eip6963Provider

  constructor({ detail, supportedChainIds }: { detail: Eip6963ProviderDetail; supportedChainIds?: number[] }) {
    super({ supportedChainIds })

    this.rdns = detail.info.rdns
    this.walletName = detail.info.name

    this.handleChainChanged = this.handleChainChanged.bind(this)
    this.handleAccountsChanged = this.handleAccountsChanged.bind(this)
    this.handleDisconnect = this.handleDisconnect.bind(this)
  }

  private resolveProvider(): Eip6963Provider {
    const detail = findEip6963ProviderByRdns(this.rdns)
    if (!detail) throw new NoEip6963ProviderError(this.walletName)
    this.provider = detail.provider
    return detail.provider
  }

  private handleChainChanged(chainId: string | number): void {
    this.emitUpdate({ chainId, provider: this.provider })
  }

  private handleAccountsChanged(accounts: string[]): void {
    if (accounts.length === 0) {
      // The user disconnected this site from inside the wallet UI.
      this.emitDeactivate()
    } else {
      this.emitUpdate({ account: accounts[0] })
    }
  }

  private handleDisconnect(): void {
    this.emitDeactivate()
  }

  public async activate(): Promise<ConnectorUpdate> {
    const provider = this.resolveProvider()

    let account: string
    try {
      const accounts = (await provider.request({ method: 'eth_requestAccounts' })) as string[]
      ;[account] = accounts
    } catch (error) {
      if ((error as { code?: number })?.code === USER_REJECTED_REQUEST) {
        throw new UserRejectedEip6963RequestError()
      }
      throw error
    }

    provider.on?.('chainChanged', this.handleChainChanged)
    provider.on?.('accountsChanged', this.handleAccountsChanged)
    provider.on?.('disconnect', this.handleDisconnect)

    const chainId = (await provider.request({ method: 'eth_chainId' })) as string

    return { provider, chainId: parseChainId(chainId), account }
  }

  public async getProvider(): Promise<Eip6963Provider> {
    return this.provider ?? this.resolveProvider()
  }

  public async getChainId(): Promise<number> {
    const provider = await this.getProvider()
    return parseChainId((await provider.request({ method: 'eth_chainId' })) as string)
  }

  public async getAccount(): Promise<string | null> {
    const provider = await this.getProvider()
    const accounts = (await provider.request({ method: 'eth_accounts' })) as string[]
    return accounts[0] ?? null
  }

  public deactivate(): void {
    const { provider } = this
    if (!provider) return

    provider.removeListener?.('chainChanged', this.handleChainChanged)
    provider.removeListener?.('accountsChanged', this.handleAccountsChanged)
    provider.removeListener?.('disconnect', this.handleDisconnect)
  }
}
